import { Navigate, Outlet, NavLink, Link } from "react-router-dom";
import { useAuth, userModules } from "./AuthContext";
import { Loader2, LogOut, Users, ClipboardList, LayoutDashboard, ExternalLink, Briefcase, Layers, CalendarClock, BarChart3, MessageSquare, Megaphone, FileCheck2 } from "lucide-react";
import { brand } from "@/content/site";

const MODULES = [
  { key: "dashboard", label: "Dashboard", to: "/admin", exact: true, Icon: LayoutDashboard },
  { key: "leads", label: "Leads", to: "/admin/leads", Icon: ClipboardList, module: "leads" },
  { key: "clients", label: "Clients", to: "/admin/clients", Icon: Briefcase, module: "clients" },
  { key: "services", label: "Services", to: "/admin/services", Icon: Layers, module: "services" },
  { key: "tasks", label: "Tasks", to: "/admin/tasks", Icon: CalendarClock, anyModule: ["leads", "clients"] },
  { key: "sms", label: "SMS", to: "/admin/sms", Icon: MessageSquare, module: "sms" },
  { key: "outreach", label: "Outreach", to: "/admin/outreach", Icon: Megaphone, module: "outreach" },
  { key: "onboarding", label: "Onboarding", to: "/admin/installer-growth-pilot/onboarding", Icon: FileCheck2, roles: ["admin", "manager"] },
  { key: "reports", label: "Reports", to: "/admin/reports", Icon: BarChart3, roles: ["admin", "manager"] },
  { key: "users", label: "Users", to: "/admin/users", Icon: Users, module: "users" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();

  if (loading || user === null) {
    return (
      <div
        data-testid="admin-loading"
        className="min-h-screen flex items-center justify-center bg-soft text-forest"
      >
        <Loader2 className="h-6 w-6 animate-spin mr-3" />
        Checking session…
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  const mods = userModules(user);

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-[#F6F8F7] flex">
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-forest text-white p-5">
        <Link to="/admin" className="flex items-center gap-2 mb-8">
          <img src={brand.logo} alt="Green Circle Group" className="h-9 w-auto invert brightness-0 opacity-0" style={{opacity:1}} />
        </Link>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-lime mb-3">
          CRM
        </p>
        <nav className="flex-1 space-y-1">
          {MODULES.filter((m) => {
            if (m.key === "dashboard") return true;
            if (m.roles && !m.roles.includes(user.role)) return false;
            if (m.anyModule) return m.anyModule.some((x) => mods.includes(x));
            if (!m.module) return true;
            return mods.includes(m.module);
          }).map((m) => {
            const Icon = m.Icon;
            return (
              <NavLink
                key={m.key}
                to={m.to}
                end={m.exact}
                data-testid={`admin-nav-${m.key}`}
                className={({ isActive }) =>
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors " +
                  (isActive
                    ? "bg-white/10 text-lime"
                    : "text-white/70 hover:bg-white/5 hover:text-white")
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{m.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="pt-5 mt-5 border-t border-white/10 space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs text-white/60 hover:text-lime"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            View public site
          </a>
          <div className="text-xs text-white/60">
            <p className="font-bold text-white/90">{user.name}</p>
            <p className="truncate">{user.email}</p>
            <p className="mt-1 inline-block text-[10px] uppercase tracking-widest text-lime">
              {user.role}
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout"
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-3 py-2"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-forest text-white p-3 flex items-center justify-between">
        <span className="text-sm font-bold text-lime">Green Circle CRM</span>
        <button
          type="button"
          onClick={logout}
          data-testid="admin-logout-mobile"
          className="text-white/80 text-xs font-semibold flex items-center gap-1"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>

      <main className="flex-1 md:pt-0 pt-14 p-5 md:p-10 max-w-full overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
